// ContractLedger.tsx
import React, { useContext, useEffect, useMemo } from "react";
import Sidebar from "../components/ui/sidebar/Sidebar";
import MainBody from "../components/ui/mainbody/MainBody";
import { SidebarContext } from "../contexts/SidebarContext";
import AuthGuard from "../components/guards/AuthGuard";
import { InlineLogisticsLoader } from "../components/ui/spinner";
import Table, { Column } from "../components/ui/table/Table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { getUserData } from "../utils/authUtils";
import { fetchUsedContract } from "../redux/slices/usedContractSlice";
import { fetchCustomerDashboard } from "../redux/slices/customerDashboardSlice";

type Row = {
  id: string;
  date: string;
  detail: string;
  credit: string;
  debit: string;
  balance: string;
  assigned_value: string;
};

const columns: Column<Row>[] = [
  { header: "Date", accessor: "date" },
  { header: "Detail", accessor: "detail" },
  { header: "Total", accessor: "assigned_value", cell: (v) => `₹${v}` },
  { header: "Debit", accessor: "debit", cell: (v) => `₹${v}` },
  { header: "Balance", accessor: "balance", cell: (v) => `₹${v}` },
];

const ContractLedger = () => {
  const { isSidebarCollapsed } = useContext(SidebarContext);
  const dispatch = useDispatch<AppDispatch>();

  const profileCustomerId = useSelector(
    (s: RootState) =>
      (s as any)?.profile?.data?.customer_id as string | undefined
  );
  const authCustomerId = useSelector(
    (s: RootState) => (s as any)?.auth?.user?.customer_id as string | undefined
  );
  const localCustomerId = useMemo(() => {
    try {
      const u = getUserData();
      return (
        u?.Customerdetail?.id ??
        (u as any)?.customer_id ??
        localStorage.getItem("customer_id") ??
        undefined
      );
    } catch {
      return localStorage.getItem("customer_id") ?? undefined;
    }
  }, []);
  const customerId = profileCustomerId ?? authCustomerId ?? localCustomerId;

  const dashLoading = useSelector(
    (s: RootState) => s.customerDashboard.loading
  );
  const activeContractId = useSelector(
    (s: RootState) => s.customerDashboard.data?.active_contract_id
  );

  const loading = useSelector((s: RootState) => s.usedContract.loading);
  const error = useSelector((s: RootState) => s.usedContract.error);
  const resp = useSelector((s: RootState) => s.usedContract.data);

  // Ensure dashboard is fetched (so we have active_contract_id)
  useEffect(() => {
    if (!customerId) return;
    if (activeContractId == null) {
      // fetch dashboard only if not present yet
      dispatch(fetchCustomerDashboard(String(customerId)));
    }
  }, [dispatch, customerId, activeContractId]);

  // Fetch usedContract when both IDs are present
  useEffect(() => {
    if (!customerId || activeContractId == null || activeContractId === 0)
      return;
    dispatch(
      fetchUsedContract({
        customer_id: String(customerId),
        contract_id: String(activeContractId),
      })
    );
  }, [dispatch, customerId, activeContractId]);

  const rows: Row[] = useMemo(() => {
    const txns = resp?.transactions ?? [];
     const total = resp?.assigned_value ?? "0";
    return txns.map((t, i) => ({
      id: `${resp?.contract_id || "c"}-${i}`,
      date: t.date,
      detail: t.detail,
      credit: t.credit,
      debit: t.debit,
      balance: t.balance,
      assigned_value: total,
    }));
  }, [resp]);

  return (
    <AuthGuard
      requireAuth
      redirectTo="/"
      allowedRoles={["customer", "admin", "user"]}
      showLoadingSpinner
    >
      <section id="trackShipmentSection">
        <div className="container-fluid p-0">
          <div
            className={`bodyWrap ${
              isSidebarCollapsed ? "sidebar-collapsed" : ""
            }`}
          >
            <Sidebar />
            <MainBody>
              <div className="container-fluid">
                <div className="tableWraper">
                  {!customerId ? (
                    <div className="text-muted py-4">Loading customer…</div>
                  ) : dashLoading && activeContractId == null ? (
                    <div className="d-flex justify-content-center py-5">
                      <InlineLogisticsLoader />
                    </div>
                  ) : loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <InlineLogisticsLoader />
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger">{error}</div>
                  ) : (
                    <>
                      <Table<Row>
                        columns={columns}
                        data={rows}
                        sectionTitle="Contract Ledger"
                        rowIdAccessor="id"
                      />
                    </>
                  )}
                </div>
              </div>
            </MainBody>
          </div>
        </div>
      </section>
    </AuthGuard>
  );
};

export default ContractLedger;
